import { Dispatch, RefObject, SetStateAction } from "react";
import {
  FieldValues,
  UseFormGetValues,
  UseFormSetValue,
} from "react-hook-form";
import { getTransferRequestFilter, TRFRProps } from "./filter-obj";

import {
  getTransferRequest,
  getTransferRequests,
  GetTransferRequestsProps,
} from "@/services/transfer-requests.service";
import { handlePermissionError } from "@/utils/permission-check";
import { AppRoutes } from "@/constants/routes";
import { toaster } from "@/components/ui/toaster";
import { AxiosError } from "axios";
import { NavigateFunction } from "react-router-dom";
import { TransferRequestDetail } from "./transfer-requests.schema";

interface ClearFiltersHandlerProps {
  setValue: UseFormSetValue<FieldValues>;
  setFiltersApplied: Dispatch<SetStateAction<boolean>>;
  submitFilters: (event: FieldValues) => Promise<void>;
  getValues: UseFormGetValues<FieldValues>;
  setOpenFilters: Dispatch<SetStateAction<boolean>>;
}

export const clearFiltersHandler = ({
  setFiltersApplied,
  setValue,
  getValues,
  submitFilters,
  setOpenFilters,
}: ClearFiltersHandlerProps) => {
  setValue("request_date", undefined);
  setValue("account_owner", "");
  setValue("request_amount", "");
  setValue("requester", "");
  setValue("origin_account", "");
  setValue("destiny_account", "");
  setValue("estados", []);

  submitFilters?.(getValues());
  setFiltersApplied(false);
  setOpenFilters(false);
};

interface SubmitFiltersHandlerProps {
  setOpenFilters: Dispatch<SetStateAction<boolean>>;
  setFiltersApplied: Dispatch<SetStateAction<boolean>>;
  setCurrentFilters: Dispatch<SetStateAction<{}>>;
  fetchTransferRequests: (filters?: any) => Promise<void>;
  event: FieldValues;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
  scheduled?: boolean;
  dateField?: "Fecha" | "FechaEjecucion";
  redirectRoute?: AppRoutes;
}

export const submitFiltersHandler = async ({
  event,
  setCurrentFilters,
  setFiltersApplied,
  fetchTransferRequests,
  setOpenFilters,
  navigate,
  fetchPermissions,
  scheduled = false,
  dateField = "Fecha",
  redirectRoute,
}: SubmitFiltersHandlerProps) => {
  try {
    setFiltersApplied(true);

    const filterObj: Partial<GetTransferRequestsProps> =
      getTransferRequestFilter(event as TRFRProps, scheduled, dateField);

    setCurrentFilters(filterObj);

    if (!Object.keys(filterObj)?.length) setFiltersApplied(false);

    await fetchTransferRequests(filterObj);

    setOpenFilters(false);
  } catch (error) {
    const errorObj = error as AxiosError<{ message: string }>;
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
      redirectRoute ??
        (scheduled
          ? AppRoutes.SCHEDULED_TRANSFER_REQUESTS
          : AppRoutes.TRANSFER_REQUESTS),
    );
    if (!handled) {
      toaster.create({
        title:
          errorObj?.response?.data?.message ??
          "No se pudieron aplicar los filtros",
        type: "error",
      });
    }
    throw error;
  }
};

export interface LoadOptionsHandlerProps {
  timeoutRef: RefObject<NodeJS.Timeout | null>;
  inputValue: string;
  setLoading: Dispatch<SetStateAction<boolean>>;
  getOptions: (inputValue: string) => Promise<any[]>;
}

export interface GetAccountOptionsHandlerProps {
  inputValue: string;
}

interface FetchTransferRequestsHandlerProps {
  filters?: any;
  showLoad?: boolean;
  setLoadingTransferRequests: Dispatch<SetStateAction<boolean>>;
  setTransferRequests: Dispatch<SetStateAction<any>>;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
}

export const fetchTransferRequestsHandler = async ({
  filters,
  showLoad,
  setLoadingTransferRequests,
  setTransferRequests,
  navigate,
  fetchPermissions,
}: FetchTransferRequestsHandlerProps) => {
  try {
    if (showLoad) setLoadingTransferRequests(true);

    const response = await getTransferRequests({
      ...filters,
      "P.Pagina": filters?.["P.Pagina"] ?? 1,
      "P.Records": 10,
    });

    setTransferRequests(response);

    setLoadingTransferRequests(false);
  } catch (error) {
    const errorObj = error as AxiosError<{ message: string }>;
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
    );
    if (!handled) {
      toaster.create({
        type: "error",
        title:
          errorObj?.message ?? "No se pudo cargar la lista de solicitudes.",
      });
    }
    throw error;
  }
};

export interface FetchTransferRequestDetailProps {
  setLoadingRequest: Dispatch<SetStateAction<boolean>>
  setRequest: Dispatch<SetStateAction<TransferRequestDetail | null | undefined>>
  navigate: NavigateFunction
  fetchPermissions: () => Promise<void>
  showLoad?: boolean
  id?: string
}

export const fetchTransferRequestDetailHandler = async ({
  fetchPermissions,
  navigate,
  setLoadingRequest,
  setRequest,
  showLoad,
  id
}: FetchTransferRequestDetailProps) => {
      try {
      if (showLoad) setLoadingRequest(true);
      const response = await getTransferRequest({
        id: Number(id),
      });
  
      setRequest(response);
  
      setLoadingRequest(false);
      } catch (error) {
        const errorObj = error as AxiosError<{ message: string }>;
        const handled = await handlePermissionError(
          error,
          navigate,
          fetchPermissions,
        );
        if (!handled) {
          toaster.create({
            type: "error",
            title:
              errorObj?.message ?? "No se pudo cargar la solicitud.",
          });
        }
        throw error;
      }
}
